import _ from 'lodash'
import Component from 'vue-class-component'
import Vue from 'vue'
import { Watch } from 'vue-property-decorator'
import IframelyEndpoint, { GetOembedResponse } from '../../api/iframely/core'
import { LinkType, OembedLink } from './UrlEditorInterface'
import ChamomileUrl from '../../misc/chamomile-url'
import { default as getStatusCode, isNotFound, isTeaPot, isUnsupportedMediaType } from '../../misc/get-status-code'
import promiseAny from '../../misc/promise-any'

interface OembedResult {
  readonly response: GetOembedResponse
  readonly url: ChamomileUrl
}

@Component({
  props: {
    value: {
      type: String,
      required: false,
      default: ''
    },
    title: {
      type: String,
      required: false,
      default: 'URLの追加'
    },
    submitLabel: {
      type: String,
      required: false,
      default: '追加'
    }
  }
})
export default class UrlEditor extends Vue {
  readonly value: string
  readonly title: string
  readonly submitLabel: string
  // vue.jsの制約により、undefined以外の初期値を必ず設定する。
  // https://jp.vuejs.org/v2/guide/reactivity.html#%E5%A4%89%E6%9B%B4%E6%A4%9C%E5%87%BA%E3%81%AE%E6%B3%A8%E6%84%8F%E4%BA%8B%E9%A0%85
  url: string = ''
  rawOembedResult: OembedResult | false = false
  loadOembed: () => void = () => {
  }
  isLoading: boolean = false
  isSaving: boolean = false
  errorMessage: string = ''
  statusCode: string = ''
  queryProtocol: string = ''

  get chamomileUrl(): ChamomileUrl | undefined {
    return ChamomileUrl.parse(this.url)
  }

  get protocol(): string {
    const chamomileUrl = this.chamomileUrl
    return chamomileUrl ? chamomileUrl.protocol : ''
  }

  get domain(): string {
    const chamomileUrl = this.chamomileUrl
    return chamomileUrl ? chamomileUrl.domain : ''
  }

  get path(): string {
    const chamomileUrl = this.chamomileUrl
    return chamomileUrl ? chamomileUrl.path : ''
  }

  get isUrlValid(): boolean {
    return !!this.chamomileUrl
  }

  get isUrlEmpty(): boolean {
    return !this.url
  }

  get canLoadOembed(): boolean {
    return this.isUrlValid && !this.isUrlEmpty
  }

  get canSubmit(): boolean {
    return !!this.rawOembedResult && !this.isLoading && this.isUrlValid
  }

  get oembedResult(): OembedResult | undefined {
    return this.canSubmit ? this.rawOembedResult || undefined : undefined
  }

  get thumbnail(): ChamomileUrl | undefined {
    return this.oembedResult ? ChamomileUrl.parse(this.oembedResult.response.thumbnail_url) : undefined
  }

  get secureThumbnail(): ChamomileUrl | undefined {
    return this.thumbnail ? this.thumbnail.toSecureUrl() : undefined
  }

  get isGoogleMap(): boolean {
    const url = this.domain + this.path
    return !!url.match(/^(?:www\.)?google(?:\.[\w]+)+\/maps/)
  }

  get hasEmbedContent(): boolean {
    const html = this.oembedResult ? this.oembedResult.response.html : undefined
    if (!html) {
      // 埋め込みコンテンツが存在しない
      return false
    }

    // 埋め込みコンテンツの中に iframely CDN を参照する URL （//cdn.iframe.ly/ ...）が含まれている場合、埋め込みさせない
    // 理由は以下の通り。
    // * iframely の api_key （または key）が公開される恐れがある（クエリパラメータに値が存在する）
    // * サイト側で記事を表示する度に iframely のアクセス数がカウントされると、課金額が嵩みそうである
    return !html.match(/<[^>]*\/\/cdn\.iframe\.ly\/[^>]*>/)
  }

  @Watch('statusCode')
  webBeacon(): string {
    const date = new Date
    return '/app/static/1x1.png?statusCode=' + this.statusCode + this.queryProtocol + '&ts=' + date.getTime()
  }

  created(): void {
    // キー入力の度に API リクエストが発生するのでは、リクエスト回数が増えすぎる。 debounce でリクエスト数を減らす。
    this.loadOembed = _.debounce(() => {
      this.doLoadOembed()
    }, 500)

    this.initializeContent()
  }

  mounted(): void {
    // URL入力欄にフォーカスを移動し、全選択する
    (<HTMLInputElement>this.$refs.url).select()
  }

  @Watch('value')
  initializeContent(): void {
    this.url = this.value
    this.doLoadOembed()
  }

  async submitChecker(): Promise<void> {
    this.isSaving = true
    try {
      await this.submit()
    } finally {
      this.isSaving = false
    }
  }

  close(): void {
    this.$emit('close')
  }

  async submit(): Promise<void> {
    if (!this.rawOembedResult) {
      throw new Error('応答がないので submit できません。')
    }
    const linkType = await this.getLinkType()
    this.$emit('submit', <OembedLink>{
      linkType,
      url: this.rawOembedResult.url.encodedUrl,
      thumbnail: (linkType === 'rich') ? this.secureThumbnail.url : undefined,
      response: this.rawOembedResult.response
    })
  }

  async getLinkType(): Promise<LinkType> {
    if (this.hasEmbedContent) {
      return 'embed'
    }
    if (await this.hasSecureThumbnail()) {
      return 'rich'
    }
    return 'simple'
  }

  async hasSecureThumbnail(): Promise<boolean> {
    const thumbnail = this.thumbnail
    if (!thumbnail) {
      return false
    }
    if (thumbnail.isSecure) {
      return true
    }
    // mixed contents の問題を避けるため、 http の画像はサムネイルとして使用しない。
    // 代わりに https の画像があれば、それをサムネイルとして採用する。
    this.queryProtocol = '&protocol=https'

    // 画像を直接リクエストするとクロスオリジン制約に引っかかるので、プロキシとして iframely を利用する。
    return await new IframelyEndpoint().getOembed({
      url: thumbnail.toSecureUrl().url
    }).then((response) => {
      this.statusCode = response.status.toString()
      return true
    }).catch((reason) => {
      this.statusCode = getStatusCode(reason).toString()
      return false
    }).then((result) => {
      return result
    })
  }

  emitInputText(): void {
    this.$emit('input')
  }

  onInputUrl(): void {
    this.rawOembedResult = false
    this.errorMessage = ''
    this.loadOembed()
  }

  doLoadOembed(): void {
    if (this.canLoadOembed) {
      this.requestOembed()
    }
  }

  async requestOembed(): Promise<void> {
    this.isLoading = true
    this.errorMessage = ''

    const url = this.chamomileUrl
    if (!url) {
      throw new Error(`有効なURLが入力されていません: ${this.url}`)
    }
    try {
      // ユーザーを待たせたくないので、セキュアなURLと非セキュアなURL、双方に同時にアクセスを試みる
      // 最終的にはセキュアなURLが優先される
      // （非セキュアなURLでセキュアなURLを上書きしないように実装している）
      const requests: Promise<void>[] = [this.doRequestOembed(url.toSecureUrl(), true)]
      if (!url.isSecure) {
        if (url.isSecure) {
          return Promise.reject(`URLがhttpsの場合、このメソッドを呼んではいけません: ${url.url}`)
        }
        requests.push(this.doRequestOembed(url, false))
      }
      await promiseAny(...requests)

    } catch (reason) {
      this.rawOembedResult = false
      this.errorMessage = '入力されたURLは不正です。もう一度URLを確認して入力してください。'

      if (isUnsupportedMediaType(reason)) {
        if (this.isGoogleMap) {
          this.errorMessage = 'GoogleMapsを埋め込みたい場合は、埋め込みコードをご利用下さい。'
        } else {
          this.errorMessage = '入力されたURLはサポートしていません。'
        }
      }
      // iFramely では セッションタイムアウトを 418( i'm a tea pot) で返答する
      if (isTeaPot(reason)) {
        this.errorMessage = '入力されたURLのページへのアクセスがタイムアウトしました。<br>時間をおいてもう一度入力してください。'
      }
      if (isNotFound(reason)) {
        this.errorMessage = '入力されたURLのページは存在しません。もう一度URLを確認して入力してください。'
      }
      // その他エラーメッセージがあればここに書く
    }
    this.isLoading = false
  }

  async doRequestOembed(url: ChamomileUrl, isSecure: boolean): Promise<void> {
    if (isSecure) {
      this.queryProtocol = '&protocol=https'
    } else {
      this.queryProtocol = '&protocol=http'
    }
    try {
      const response = await new IframelyEndpoint().getOembed({
        url: url.encodedUrl
      })

      // 入力中のURLがリクエストの値と異なる場合、リクエストの結果は無視する
      const inputUrl = isSecure ? this.chamomileUrl.toSecureUrl().encodedUrl : this.chamomileUrl.encodedUrl
      if (!isSecure && !this.chamomileUrl || inputUrl !== url.encodedUrl) {
        throw  Error()
      }

      // https のリクエストが成功したが title が含まれていない場合、
      // ページが存在しない可能性が高いのでリクエストの結果は無視する
      if (isSecure && !response.data.title && response.data.type === 'link') {
        throw  Error()
      }

      // httpでのアクセスで既にセキュアなURLが読み込み成功しているときは、上書きしない
      if (!isSecure && !!this.rawOembedResult && this.rawOembedResult.url.encodedUrl === url.toSecureUrl().encodedUrl) {
        return
      }

      this.rawOembedResult = {
        url,
        response: response.data
      }

      this.statusCode = response.status.toString()

    } catch (response) {
      this.statusCode = getStatusCode(response).toString()
      throw response
    }
  }
}
